import { Button, Card, RefreshIcon } from '@/lib';
import React, { FC, useState } from 'react';
import { XCircleIcon } from '@heroicons/react/solid';
import { MarketIntelligenceReportData } from '../../types';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { CREDIT_SALES_CONTACT, useMarketIntelligenceCreditCheck } from '@/features/campaigns/v2/credits';
import { Tooltip } from '@/components/tooltips';
import { BaseModal } from '@/lib/Modals';

const REPORT_META_ITEMS = [
  { id: 'analyzedDomain', translationKey: 'report.meta.analyzedDomain' },
  { id: 'industry', translationKey: 'report.meta.industry' },
  { id: 'scopeLabel', translationKey: 'report.meta.scope' },
  { id: 'generatedAt', translationKey: 'report.meta.generatedAt' },
] as const satisfies ReadonlyArray<{
  id: keyof Pick<MarketIntelligenceReportData, 'analyzedDomain' | 'industry' | 'scopeLabel' | 'generatedAt'>;
  translationKey: string;
}>;

const DISABLED_TOOLTIP_CLASS =
  'z-[100] max-w-[340px] rounded-[10px] border-0 bg-[#E8ECF2] px-4 py-3.5 text-[13px] font-normal leading-[1.45] text-ink-800 shadow-none';

const ReportMetaItem: FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className='min-w-0'>
    <p className='text-primary-900 truncate text-sm font-semibold'>{value}</p>
    <p className='text-xs text-primary-600'>{label}</p>
  </div>
);

export const ReportMetaItems = ({
  report,
  onRegenerate,
  isGenerating,
}: {
  report: MarketIntelligenceReportData;
  onRegenerate: () => void | Promise<void>;
  isGenerating: boolean;
}) => {
  const { t } = useTranslation('marketIntelligence');
  const { available, isKnown, canUseFeature, isOutOfCredits } = useMarketIntelligenceCreditCheck();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);

  const isRegenerateDisabled = isGenerating || !canUseFeature || isConfirming;
  const regenerateDisabledTooltip = isGenerating
    ? t('report.regenerateDisabled.inProgress')
    : isOutOfCredits
      ? t('report.regenerateDisabled.outOfCredits')
      : '';

  const handleOpenConfirm = () => {
    if (isRegenerateDisabled) {
      return;
    }
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    if (isConfirming) {
      return;
    }
    setIsConfirmOpen(false);
  };

  const handleConfirmRegenerate = async () => {
    if (isConfirming) {
      return;
    }

    setIsConfirming(true);
    setIsConfirmOpen(false);

    try {
      await onRegenerate();
    } finally {
      setIsConfirming(false);
    }
  };

  const regenerateButton = (
    <Button intent='secondary' size='md' onClick={handleOpenConfirm} disabled={isRegenerateDisabled}>
      <RefreshIcon className={cn('size-4', (isGenerating || isConfirming) && 'animate-spin')} />
      <span>{t('report.actions.regenerate')}</span>
    </Button>
  );

  return (
    <>
      <div className='flex w-full flex-col gap-3'>
        <Card className='block border-primary-border bg-white p-6 drop-shadow-none'>
          <div className='flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between'>
            <div className='grid flex-1 gap-4 sm:grid-cols-2 xl:grid-cols-4'>
              {REPORT_META_ITEMS.map((item) => (
                <ReportMetaItem key={item.id} value={report[item.id] || '-'} label={t(item.translationKey)} />
              ))}
            </div>
            <div className='flex min-w-0 flex-col items-stretch sm:items-end'>
              {isRegenerateDisabled && regenerateDisabledTooltip ? (
                <Tooltip className={DISABLED_TOOLTIP_CLASS} tooltipContent={regenerateDisabledTooltip} place='top' noArrow>
                  <span className='inline-flex'>{regenerateButton}</span>
                </Tooltip>
              ) : (
                regenerateButton
              )}
            </div>
          </div>
        </Card>

        {isOutOfCredits ? (
          <div className='flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800' role='alert'>
            <XCircleIcon className='mt-0.5 size-5 shrink-0 text-red-600' aria-hidden />
            <p>{t('report.credits.unavailable', { email: CREDIT_SALES_CONTACT.email })}</p>
          </div>
        ) : null}
      </div>

      {isConfirmOpen ? (
        <BaseModal onClose={handleCloseConfirm} isDismissable={!isConfirming}>
          <div>
            <h2 className='text-lg font-semibold text-primary-900'>{t('report.regenerateConfirm.title')}</h2>
            <div className='mt-2 space-y-1 text-sm leading-5 text-primary-600'>
              {isKnown ? (
                <>
                  <p>{t('report.regenerateConfirm.usesCredit')}</p>
                  <p>{t('report.regenerateConfirm.remaining', { count: Math.max(available - 1, 0) })}</p>
                </>
              ) : null}
              <p>{t('report.regenerateConfirm.replacesCurrent')}</p>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-3 pt-4 sm:grid-cols-2'>
            <Button intent='secondary' size='lg' text={t('report.regenerateConfirm.cancel')} onClick={handleCloseConfirm} disabled={isConfirming} />
            <Button
              intent='primary'
              size='lg'
              text={t('report.regenerateConfirm.confirm')}
              onClick={handleConfirmRegenerate}
              disabled={isConfirming}
              isLoading={isConfirming}
            />
          </div>
        </BaseModal>
      ) : null}
    </>
  );
};
